import { HttpBadStatusCodeError, HttpBodyParseError, HttpTimeoutError, UnknownError } from '@cian/core/errors';
import { ResponseError } from 'shared/map-search/utils/errors/ResponseError';

import { EClustersFailReason } from '../constants';

const SERVER_ERROR_STATUS_CODE = 500;

/**
 * Классифицирует ошибку загрузки кластеров для сегментации метрики fail.
 *
 * Классифицируем только то, что опознаём уверенно. Всё остальное уходит в Unknown —
 * по его доле видно, что стоит доклассифицировать.
 *
 * Проверяем через `is`, а не через `isLike`: `isLike` сравнивает
 * `Object.getPrototypeOf(error).constructor.name` с именем класса, а в продовой сборке имена
 * классов минифицируются, и сравнение перестаёт срабатывать. Из-за этого первые сутки сбора
 * все отказы, кроме нашего собственного ResponseError, уходили в Unknown. `is` сравнивает поле
 * `type` — это строковая константа, минификация её не трогает.
 */
export function resolveFailReason(error: unknown): EClustersFailReason {
  if (HttpTimeoutError.is(error)) {
    return EClustersFailReason.Timeout;
  }

  if (HttpBodyParseError.is(error)) {
    return EClustersFailReason.BadPayload;
  }

  /**
   * Ответ 400 приходит от ручки кластеров как валидный типизированный ответ
   * и превращается в ResponseError в assertUnknownError, поэтому до HttpBadStatusCodeError не доходит.
   */
  if (error instanceof ResponseError) {
    return EClustersFailReason.BadRequest;
  }

  if (HttpBadStatusCodeError.is(error)) {
    return error.statusCode >= SERVER_ERROR_STATUS_CODE ? EClustersFailReason.Http5xx : EClustersFailReason.Http4xx;
  }

  /**
   * В UnknownError http-клиент заворачивает всё, на чём споткнулся сам fetch: обрыв связи,
   * недоступный DNS, отказ по CORS. Ответа не было вовсе, и на нашей стороне это не чинится,
   * поэтому причину стоит видеть отдельно от действительно неопознанных ошибок.
   */
  if (UnknownError.is(error)) {
    return EClustersFailReason.Network;
  }

  return EClustersFailReason.Unknown;
}
